var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { paddingForAspectRatio } from './utils/paddingForAspectRatio';
export const transparentDataImg = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
const defaultImageState = {
    imgLoaded: false,
    error: false
};
const loadedImageState = {
    imgLoaded: true,
    error: false
};
let hydrated = false;
const imageCache = Object.create({});
function isInCache(src) {
    return src in imageCache;
}
function cacheImage(src) {
    imageCache[src] = true;
}
function imageStateReducer(_, action) {
    switch (action.type) {
        case 'reset':
            return defaultImageState;
        case 'loaded':
            cacheImage(action.cacheKey);
            return loadedImageState;
        case 'error':
            return { imgLoaded: false, error: true };
        default:
            console.error(`Action ${action.type} is invalid for image reducer`);
            return;
    }
}
function useObjectFitPolyfill(imageRefs) {
    const [polyfilled, setPolyfilled] = React.useState(false);
    React.useEffect(() => {
        const testImg = document.createElement(`img`);
        if (typeof testImg.style.objectFit === `undefined` ||
            typeof testImg.style.objectPosition === `undefined`) {
            import(`object-fit-images`).then(({ default: ObjectFitImages }) => {
                imageRefs.forEach((imageRef) => {
                    imageRef.current && ObjectFitImages(imageRef.current);
                });
                setPolyfilled(true);
            });
        }
    }, [imageRefs]);
    return polyfilled;
}
function getTypeByExtension(src) {
    // Identifying type by extension, based on common image file types
    // https://developer.mozilla.org/en-US/docs/Web/Media/Formats/Image_types
    const extensionRegex = /\.(jpg|jpeg|jfif|pjpeg|pjp|png|gif|bmp|webp|svg|apng|ico|cur)(?:\?.*)?$/i;
    const formatMatch = extensionRegex.exec(src);
    if (!formatMatch)
        return null;
    let type = null;
    switch (formatMatch[1]) {
        case 'jpg':
        case 'jpeg':
        case 'jfif':
        case 'pjpeg':
        case 'pjp':
            type = 'image/jpeg';
            break;
        case 'png':
            type = 'image/png';
            break;
        case 'gif':
            type = 'image/gif';
            break;
        case 'bmp':
            type = 'image/bmp';
            break;
        case 'webp':
            type = 'image/webp';
            break;
        case 'svg':
            type = 'image/svg+xml';
            break;
        case 'apng':
            type = 'image/apng';
            break;
        case 'ico':
        case 'cur':
            type = 'image/x-icon';
            break;
        default:
            break;
    }
    return type;
}
const Img = React.forwardRef((props, ref) => {
    const { sizes, srcSet, src, style, alt, onLoad, onError, objectFit, critical } = props, otherProps = __rest(props, ["sizes", "srcSet", "src", "style", "alt", "onLoad", "onError", "objectFit", "critical"]);
    const imgStyle = Object.assign({ position: 'absolute', top: 0, left: 0, width: `100%`, height: `100%`, paddingTop: 0, objectPosition: `center`, objectFit }, style);
    return (React.createElement("img", Object.assign({ alt: alt, sizes: sizes, srcSet: srcSet, src: src, onLoad: onLoad, onError: onError, loading: critical ? 'eager' : 'lazy' }, otherProps, { ref: ref, style: imgStyle })));
});
const Placeholder = React.forwardRef((props, ref) => {
    return (React.createElement("picture", { "data-flight-image-placeholder": "" },
        React.createElement(Img, Object.assign({ loading: "lazy", alt: "" }, props, { ref: ref }))));
});
const Image = function Image(_a) {
    var { src, hasFalsySrc, lqip, sizes, srcset, webpSrcset, critical, aspect, cover = false, alt = '', fillAvailableSpace = false, children, error: ErrorComp, className, badges } = _a, rest = __rest(_a, ["src", "hasFalsySrc", "lqip", "sizes", "srcset", "webpSrcset", "critical", "aspect", "cover", "alt", "fillAvailableSpace", "children", "error", "className", "badges"]);
    // Set up the IntersectionObserver
    const [ref, inView] = useInView({ triggerOnce: true });
    // Check if the browser supports loading="lazy". In that case, we don't need
    // the IntersectionObserver
    const nativeLazyLoading = typeof HTMLImageElement !== 'undefined' &&
        'loading' in HTMLImageElement.prototype;
    const imageRef = React.createRef();
    const placeholderRef = React.createRef();
    useObjectFitPolyfill([imageRef, placeholderRef]);
    const [{ imgLoaded, error }, dispatch] = React.useReducer(imageStateReducer, critical ? loadedImageState : defaultImageState);
    const cacheKey = srcset;
    const handleImgLoad = React.useCallback(function handleImgLoad() {
        dispatch({ type: 'loaded', cacheKey });
    }, [cacheKey]);
    function handleImgError() {
        dispatch({ type: 'error' });
    }
    // Treat critical images as always cached
    const isCached = critical || isInCache(cacheKey);
    React.useEffect(() => {
        hydrated = true;
    }, []);
    React.useEffect(() => {
        dispatch({ type: 'reset' });
    }, [src]);
    React.useEffect(() => {
        // Handle case where image onLoad fires during SSR
        if (imageRef.current && imageRef.current.complete && !isCached) {
            handleImgLoad();
        }
    }, [handleImgLoad, imageRef, isCached]);
    // Render nothing if src is boolean
    if (typeof src === 'boolean' && !src) {
        return null;
    }
    const loaded = imgLoaded || hasFalsySrc || isCached;
    const objectFit = cover || fillAvailableSpace ? 'cover' : 'contain';
    const objectFitPolyfillStyles = { fontFamily: `"object-fit: ${objectFit}"` };
    const type = getTypeByExtension(src);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", Object.assign({ key: src, ref: 
            // If we have native lazyloading, by not applying the `ref` from
            // useInView we don't use IntersectionObserver at all
            nativeLazyLoading ? undefined : ref, "data-flight-image-container": "", "data-flight-image-loaded": loaded, className: className }, rest, { style: {
                position: `relative`,
                overflow: `hidden`,
                width: `100%`,
                height: fillAvailableSpace ? '100%' : 'auto',
                paddingBottom: fillAvailableSpace
                    ? 0
                    : !aspect
                        ? '100%'
                        : paddingForAspectRatio(aspect)
            } }),
            error ? (React.createElement(ErrorComp, null)) : (React.createElement(React.Fragment, null,
                React.createElement(Placeholder, { src: critical ? transparentDataImg : lqip || transparentDataImg, ref: placeholderRef, style: Object.assign({ objectFit, opacity: loaded ? 0 : 0.6, transitionDelay: `500ms` }, objectFitPolyfillStyles) }),
                (inView || nativeLazyLoading || !hydrated || isCached) && (React.createElement("picture", { "data-flight-image": "" },
                    React.createElement("source", { type: "image/webp", srcSet: webpSrcset, sizes: sizes }),
                    React.createElement("source", { type: type, srcSet: srcset, sizes: sizes }),
                    React.createElement(Img, { srcSet: srcset, src: src || transparentDataImg, sizes: sizes, onLoad: handleImgLoad, onError: handleImgError, alt: alt, ref: imageRef, style: Object.assign({ objectFit, opacity: loaded ? 1 : 0, transition: `opacity 500ms` }, objectFitPolyfillStyles), critical: critical }))),
                React.createElement("noscript", null,
                    React.createElement("picture", null,
                        React.createElement(Img, { srcSet: srcset, src: src, sizes: sizes, alt: alt, style: {
                                objectFit
                            }, critical: critical }))))),
            children && (React.createElement("div", { "data-flight-image-children": "", style: {
                    position: 'relative',
                    zIndex: 2,
                    width: '100%',
                    height: '100%'
                } }, children)),
            badges && (React.createElement("div", { "data-flight-image-badges": "", style: {
                    zIndex: 2
                } }, badges)))));
};
export default Image;
//# sourceMappingURL=BaseImage.js.map